import { type ReactNode, useCallback } from "react";
import type { GestureResponderEvent } from "react-native";
import { AnimatePresence, type ColorTokens, type GetProps, isWeb, styled, View, XStack } from "tamagui";
import { HapticsService, useMinimumLoadingTime } from "../../utils";
import { Icon, type IconComponent } from "../Icon";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { Loader } from "../Loader";
import { Interface1, Interface2, Interface3 } from "../Typography";
import { BrandButtonGradient } from "./BrandButtonGradient";
import type { ButtonSize, ButtonVariant } from "./types";
import { useBrandButtonGradient } from "./useBrandButtonGradient";
import { buttonSizeToIconToken, buttonVariants, disabledIconColorTokens, disabledTextColorTokens, disabledVariantStyles, iconColorTokens, iconOnlySizes, iconPaddingBySize, textColorTokens } from "./utils";
export type { ButtonSize, ButtonVariant } from "./types";
const StyledButton = styled(View, {
  name: "Button",
  render: "button",
  focusable: true,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "$brFull",
  // `position: relative` + `overflow: hidden` create a stacking context so the
  // BrandButtonGradient overlay stays clipped within the button's rounded bounds.
  position: "relative",
  overflow: "hidden",
  // CSS transitions handle web; native uses Moti via animation prop on the instance
  style: {
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "100ms",
    transitionTimingFunction: "ease-out"
  },
  variants: buttonVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium",
    fillWidth: true
  } as const
});
const opacityTransitionStyle = {
  transitionProperty: "opacity",
  transitionDuration: "100ms",
  transitionTimingFunction: "ease-out"
};

// Native-only animation props for opacity; on web, CSS transitions handle it.
const nativeOpacityAnimation = isWeb ? {} : {
  transition: "100ms easeOut" as const,
  animateOnly: ["opacity"]
};
const buttonTextStyles = {
  name: "ButtonText",
  style: opacityTransitionStyle
};
const ButtonTextLarge = styled(Interface1, buttonTextStyles);
const ButtonTextMedium = styled(Interface2, buttonTextStyles);
const ButtonTextSmall = styled(Interface3, buttonTextStyles);
const IconWrapper = styled(View, {
  name: "IconWrapper",
  style: opacityTransitionStyle
});

// Overlay that fades in/out with AnimatePresence when the button is loading.
// Absolute so it covers the hidden (opacity: 0) text/icons without affecting layout.
const LoaderOverlay = styled(View, {
  name: "LoaderOverlay",
  position: "absolute",
  inset: 0,
  justifyContent: "center",
  alignItems: "center",
  opacity: 1,
  enterStyle: {
    opacity: 0
  },
  exitStyle: {
    opacity: 0
  },
  style: opacityTransitionStyle
});
type StyledButtonProps = GetProps<typeof StyledButton>;
type BaseButtonProps = Omit<StyledButtonProps, "variant" | "size" | "onPressOut" | "children" | "onPress" | "href" | "render" | "role"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Skip the minimum loading display time (400ms) and transition instantly.
   * @default false
   */
  instantLoading?: boolean;
};

/**
 * Icon-only buttons: pass `aria-label` for screen readers.
 * Omit only for purely decorative icons.
 */
type IconOnlyButtonProps = BaseButtonProps & {
  icon: IconComponent;
  children?: never;
  iconLeft?: never;
  iconRight?: never;
};
type TextButtonProps = BaseButtonProps & {
  icon?: never;
  children: ReactNode;
  iconLeft?: IconComponent;
  iconRight?: IconComponent;
};
type VisualProps = IconOnlyButtonProps | TextButtonProps;
type BehaviorProps = {
  onPress?: (event: GestureResponderEvent) => void;
  href?: string;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
};
export type ButtonProps = VisualProps & BehaviorProps;
export const Button = (props: ButtonProps) => {
  const {
    variant = "primary",
    size = "medium",
    loading,
    instantLoading = false,
    disabled,
    icon,
    iconLeft,
    iconRight,
    fillWidth = true,
    children,
    onPress,
    href,
    replace,
    scroll,
    shallow,
    testID,
    ...rest
  } = props;
  const isIconOnly = icon !== undefined;
  const defaultTestID = typeof children === "string" ? `${variant}-button-${children}` : `${variant}-button`;
  const resolvedTestID = testID ?? defaultTestID;
  const debouncedLoading = useMinimumLoadingTime(loading);
  const showLoading = instantLoading ? loading ?? false : debouncedLoading;
  const isInteractionDisabled = disabled || showLoading;
  const isBrand = variant === "brand";
  const brandGradient = useBrandButtonGradient({
    variant,
    isInteractionDisabled
  });

  // Always interactive — Button renders as a real `<button>` (or `<a>` for
  // href) with focus + keyboard wiring even when no `onPress` is supplied.
  const {
    interactiveProps,
    renderTag
  } = useInteractiveElement({
    kind: href !== undefined ? "link" : "button",
    onPress,
    href,
    replace,
    scroll,
    shallow,
    disabled: isInteractionDisabled
  });
  const handlePressOut = useCallback(() => {
    brandGradient.handleBrandPressOut();
    if (!isInteractionDisabled) {
      void HapticsService.selection();
    }
  }, [brandGradient.handleBrandPressOut, isInteractionDisabled]);
  const buttonTextBySize = {
    large: ButtonTextLarge,
    medium: ButtonTextMedium,
    small: ButtonTextSmall
  };
  const ButtonText = buttonTextBySize[size];
  const iconSize = buttonSizeToIconToken[size];
  const textColor: ColorTokens = disabled ? disabledTextColorTokens[variant] : textColorTokens[variant];
  const iconColor: ColorTokens = disabled ? disabledIconColorTokens[variant] : iconColorTokens[variant];
  const disabledStyles = disabled ? disabledVariantStyles[variant] : {};

  // For icon-only buttons, make width equal to height (square) and remove padding
  const iconOnlyStyles = isIconOnly ? {
    width: iconOnlySizes[size],
    paddingHorizontal: 0
  } : {};

  // Asymmetric padding when icons present (optically centered per design spec).
  // Use reduced padding on whichever side has an icon.
  const hasIcons = !isIconOnly && (iconLeft || iconRight);
  const iconPaddingStyles = hasIcons ? {
    paddingLeft: iconLeft ? iconPaddingBySize[size].iconSide : iconPaddingBySize[size].textSide,
    paddingRight: iconRight ? iconPaddingBySize[size].iconSide : iconPaddingBySize[size].textSide
  } : {};
  return <StyledButton {...rest} testID={resolvedTestID} render={renderTag ?? "button"} variant={variant} size={size} disabled={isInteractionDisabled} fillWidth={isIconOnly ? false : fillWidth}
  // Gradient handlers only for brand — avoids registering ~10 noop DOM listeners on every non-brand button.
  {...isBrand && {
    onLayout: brandGradient.handleBrandLayout,
    onMouseEnter: brandGradient.handleBrandMouseEnter,
    onMouseMove: brandGradient.handleBrandMouseMove,
    onMouseLeave: brandGradient.handleBrandMouseLeave,
    onTouchStart: brandGradient.handleBrandTouchStart,
    onTouchMove: brandGradient.handleBrandTouchMove,
    onTouchEnd: brandGradient.handleBrandTouchEnd,
    onTouchCancel: brandGradient.handleBrandTouchEnd,
    onResponderRelease: brandGradient.handleBrandTouchEnd,
    onResponderTerminate: brandGradient.handleBrandTouchEnd,
    onPressIn: brandGradient.handleBrandPressIn
  }} onPressOut={handlePressOut} {...disabledStyles} {...iconOnlyStyles} {...iconPaddingStyles} {...isInteractionDisabled && {
    cursor: "not-allowed"
  }} {...interactiveProps} {...!isWeb && {
    // TODO: Re-enable transition/animateOnly props once background color animation
    // is fixed (GRO-474).
  }} data-sentry-element="StyledButton" data-sentry-component="Button" data-sentry-source-file="Button.tsx">
      {isBrand ? <BrandButtonGradient visible={brandGradient.showBrandHoverGradient} style={brandGradient.brandGradientStyle} /> : null}
      <XStack justifyContent="center" alignItems="center" gap="$sp1" position="relative" zIndex={1} pointerEvents="none" data-sentry-element="XStack" data-sentry-source-file="Button.tsx">
        {icon ? <IconWrapper opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation} style={{
        outline: "none",
        pointerEvents: "none"
      }}>
            <Icon icon={icon} size={iconSize} color={iconColor} />
          </IconWrapper> : null}
        {iconLeft ? <IconWrapper opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation}>
            <Icon icon={iconLeft} size={iconSize} color={iconColor} />
          </IconWrapper> : null}
        {children ? <ButtonText color={textColor} opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation}>
            {children}
          </ButtonText> : null}
        {iconRight ? <IconWrapper opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation}>
            <Icon icon={iconRight} size={iconSize} color={iconColor} />
          </IconWrapper> : null}
        <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="Button.tsx">
          {showLoading ? <LoaderOverlay {...nativeOpacityAnimation}>
              <Loader color={textColor} size={iconSize} />
            </LoaderOverlay> : null}
        </AnimatePresence>
      </XStack>
    </StyledButton>;
};